import { Row, Typography } from 'antd';
import { Link } from 'react-router-dom';
import i18n from 'i18next';
import { isMobileOnly } from 'react-device-detect';
import { Trans } from 'react-i18next';
import cn from 'classnames';

import { CountryFlag, Popover, Tooltip } from 'componets/ui';
import { InvestmentAgreementDownload } from './InvestmentAgreementDownload';
import { InvestmentSecondaryMarket } from './InvestmentSecondaryMarket';
import { InvestmentSaleStatus } from './InvestmentSaleStatus';
import { DropDownComponent } from 'componets/common';

import { IMyInvestments, IMyInvestmentsSecondaryMarket } from 'models';

import Utils from 'utils/utils';
import { RequestStatus } from 'utils/constants';

import { ReactComponent as InfoIcon } from 'assets/icons/info-grey.svg';

import s from './index.module.scss';

const { t } = i18n;

export const loanStatusColumn = {
	title: t('loanStatusText'),
	dataIndex: 'loanStatus',
	width: 175,
	render: (loanStatus: string) => {
		if (!loanStatus) {
			return (
				<Typography.Text type="secondary">
					<>{t('notSetText')}</>
				</Typography.Text>
			);
		}
		const isRecovery = loanStatus === 'IN_RECOVERY';
		const status = t(loanStatus);
		return isRecovery ? (
			<Row align="middle" style={{ gap: '5px' }}>
				{status}
				<Tooltip title={t('inRecoveryIconText') as string}>
					<InfoIcon />
				</Tooltip>
			</Row>
		) : (
			status
		);
	},
};

export const columnsTable = [
	{
		title: `ID`,
		dataIndex: 'id',
		sorter: true,
		width: 80,
		render: (v: any) => (
			<Link to={`/account/investments/${v}`} className={s.linkId}>
				{v}
			</Link>
		),
	},
	{
		title: `${t('projectText')}`,
		dataIndex: 'projectTitle',
		sorter: true,
		width: 200,
		render: (projectTitle: string, row: IMyInvestments) => {
			return (
				<div>
					<div className={s.row}>
						<Popover content={row.country.title} trigger="hover">
							<div className={s.flag}>
								<CountryFlag country={row.country.iso} />
							</div>
						</Popover>
						<Link to={`/account/projects/${row.loanId}`} className={cn(s.link, s.id, s.linkId)}>
							{row.loanId}
						</Link>
					</div>
					&nbsp;
					<div>
						<Link to={`/account/projects/${row.loanId}`} className={cn(s.link, s.linkId)}>
							{row.project.title}
						</Link>
					</div>
				</div>
			);
		},
	},
	{
		title: t('loanStatusText'),
		dataIndex: 'status',
		width: 175,
		render: (loanStatus: string) => {
			if (!loanStatus) {
				return (
					<Typography.Text type="secondary">
						<>{t('notSetText')}</>
					</Typography.Text>
				);
			}
			const isRecovery = loanStatus === 'IN_RECOVERY';
			const status = t(loanStatus);
			return isRecovery ? (
				<Row align="middle" style={{ gap: '5px' }}>
					{status}
					<Tooltip title={t('inRecoveryIconText') as string}>
						<InfoIcon />
					</Tooltip>
				</Row>
			) : (
				status
			);
		},
	},
	{
		title: t('nextPaymentStatusText'),
		dataIndex: 'nextPaymentStatus',
		width: 150,
		render: (nextPaymentStatus: 'LATE' | 'SCHEDULED' | null) => {
			return nextPaymentStatus !== null ? (
				t(nextPaymentStatus)
			) : (
				<Typography.Text type="secondary">{t('notSetText') as string}</Typography.Text>
			);
		},
	},
	{
		title: t('marketText'),
		dataIndex: 'secondaryMarket',
		width: 125,
		render: (secondaryMarket: IMyInvestmentsSecondaryMarket) => {
			return `${t(secondaryMarket.purchased ? 'secondaryText' : 'primaryText')}`;
		},
	},
	{
		title: t('dateOfInvestmentText'),
		dataIndex: 'dateOfPurchase',
		sorter: true,
		width: 155,
		textWrap: 'word-break',
	},
	{
		title: t('projectTypeText'),
		dataIndex: 'loanType',
		render: (loanType: string) => {
			return <Typography.Text>{Utils.formatProjectType(loanType)}</Typography.Text>;
		},
		width: 160,
		textWrap: 'word-break',
	},
	{
		title: t('interestRateText'),
		dataIndex: 'interestRate',
		render: (interest: number, row: IMyInvestments) => {
			return `${row.interest}%`;
		},
		sorter: true,
		width: 110,
		textWrap: 'word-break',
	},
	{
		title: t('investedAmountText'),
		dataIndex: 'amount',
		render: (amount: number, row: IMyInvestments) => {
			return (
				<div className={s.amount}>
					{Utils.moneyFormat(amount)}
					{row.coupon && (
						<Tooltip
							className={s.popover}
							title={
								<Trans
									values={{ code: row.coupon.code }}
									i18nKey={
										row.coupon.investmentIsOriginalInvestment
											? 'couponPromoCodeText'
											: 'couponDakarText'
									}
								/>
							}
						>
							<InfoIcon />
						</Tooltip>
					)}
					{row?.referralFee && (
						<Tooltip
							className={s.popover}
							title={
								<Trans i18nKey={'referralFeePromoText'} />
							}
						>
							<InfoIcon />
						</Tooltip>
					)}
				</div>
			);
		},
		sorter: true,
		width: 120,
		textWrap: 'word-break',
	},
];

export const receivedPaymentColumns = [
	{
		title: t('receivedPaymentsText'),
		dataIndex: 'receivedPayments',
		render: (receivedPayments: number) => {
			return Utils.moneyFormat(receivedPayments);
		},
		width: 130,
		className: 'receivedPaymentsColumn',
		fixed: isMobileOnly ? '' : 'right',
	},
	{
		title: t('investmentDocumentsText'),
		dataIndex: 'secondaryMarket',
		render: (secondaryMarket: IMyInvestmentsSecondaryMarket, row: IMyInvestments) => {
			const isBuyer = secondaryMarket.agreements.buyerAgreement;
			const isSeller = secondaryMarket.agreements.sellerAgreement;

			const isTwo = isBuyer && isSeller;

			return (
				<DropDownComponent
					placement="bottom"
					trigger={isMobileOnly ? 'click' : 'hover'}
					className={s.dropdown}
					isTable
					dropdownRender={() => (
						<div className={s.dropdownWrapper}>
							<InvestmentAgreementDownload id={row.id} label={t('investmentAgreementText')} />

							{isBuyer && (
								<InvestmentAgreementDownload
									type="buyer"
									id={row.id}
									label={t('assigmentAgreementText', { number: isTwo ? '#1' : '' })}
								/>
							)}

							{isSeller && (
								<InvestmentAgreementDownload
									type="seller"
									id={row.id}
									label={t('assigmentAgreementText', { number: isTwo ? '#2' : '' })}
								/>
							)}
						</div>
					)}
					label={t('viewText')}
				/>
			);
		},

		width: 110,
		className: 'agreementColumn',
		fixed: isMobileOnly ? '' : 'right',
	},
];

export const cartIconColumn = {
	title: '',
	dataIndex: 'secondaryMarket',
	render: (secondaryMarket: IMyInvestmentsSecondaryMarket, row: IMyInvestments) => {
		const isSell = secondaryMarket.canBeSold && !secondaryMarket.activeSale;
		const isPending = secondaryMarket.activeSale?.status === RequestStatus.PENDING.toUpperCase();
		const isCancelSell = secondaryMarket.canBeSold && !!secondaryMarket.activeSale && !isPending;

		return (
			<InvestmentSecondaryMarket
				isPending={isPending}
				id={row.id}
				projectId={row.loanId}
				isSell={isSell}
				isCancelSell={isCancelSell}
				activeSaleId={secondaryMarket.activeSale?.id}
			/>
		);
	},
	width: 65,
	fixed: isMobileOnly ? '' : 'right',
};

export const soldColumn = {
	title: t('statusText'),
	dataIndex: 'secondaryMarket',
	render: (secondaryMarket: IMyInvestmentsSecondaryMarket, row: IMyInvestments) => (
		<InvestmentSaleStatus secondaryMarket={secondaryMarket} refunded={row.refund?.refunded} />
	),
	width: 130,
	fixed: isMobileOnly ? '' : 'right',
};
